import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Box, Card, Chip, IconButton, Tooltip, Typography, alpha } from '@mui/material';
import { Link, useViewTransitionState } from 'react-router';

import { CodigoDePieza } from '@/components/CodigoDePieza';
import {
  enPesos,
  enPorcentaje,
  estaEnOferta,
  precioParaMostrar,
  tieneDescuentoPorBulto,
} from '@/lib/catalogo';
import type { Producto } from '@/tipos';

interface Props {
  producto: Producto;
  /** La foto principal, si la tiene. Viene resuelta del servidor junto con el producto. */
  foto: string | null;
  /** Los precios sólo se ven con sesión. Lo decide el servidor, no esta propiedad. */
  conPrecios: boolean;
  /** Abre la vista rápida. Sin esto la tarjeta no muestra el botón: la ficha sigue a un clic. */
  onVistaRapida?: (producto: Producto) => void;
}

export function TarjetaDeProducto({ producto, foto, conPrecios, onVistaRapida }: Props) {
  const precio = producto.price;
  const ficha = `/catalogo/${encodeURIComponent(producto.code)}`;

  // Cierto sólo para la tarjeta que se está abriendo. El nombre de una transición tiene que ser
  // único en la página: si las doce tarjetas de la grilla lo llevaran siempre, el navegador
  // encuentra doce elementos con el mismo nombre y descarta la animación entera.
  const viajando = useViewTransitionState(ficha);

  const { descuento, conOferta, mostrarOferta } = precioParaMostrar(producto);

  return (
    <Card
      sx={tema => ({
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1e1e1e',

        // El borde arranca transparente y ocupa lugar desde el principio: si apareciera recién
        // al pasar por encima, la tarjeta crecería un pixel y la grilla entera temblaría.
        border: '1px solid transparent',

        // La curva no es `ease`: arranca rápido y frena al final, que es como se mueve algo que
        // tiene peso. Con `ease` el movimiento se siente mecánico.
        transition:
          'transform .35s cubic-bezier(.22,.61,.36,1), box-shadow .35s ease, border-color .35s ease',

        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 14px 30px rgba(0,0,0,.45)',
          borderColor: alpha(tema.palette.primary.main, 0.35),
        },

        // La foto se acerca apenas dentro de su recuadro. Es lo que hace que la tarjeta se
        // sienta viva sin que nada cambie de lugar: el contenedor recorta y no crece.
        '&:hover .foto-del-producto': { transform: 'scale(1.06)' },
        '&:hover .vista-rapida': { opacity: 1 },

        // A quien pidió que las cosas no se muevan, no se le mueve nada. El color se queda.
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'border-color .35s ease',
          '&:hover': { transform: 'none' },
          '&:hover .foto-del-producto': { transform: 'none' },
        },
      })}
    >
      <Box
        component={Link}
        to={ficha}
        viewTransition
        // La foto lleva a la ficha igual que el texto. Antes el enlace empezaba recién debajo
        // del recuadro, así que hacer clic en la imagen -que es lo primero que se mira- no
        // hacía nada y el cursor seguía siendo una flecha.
        //
        // Va sin foco propio y oculta al lector de pantalla: el enlace del texto, acá abajo, ya
        // dice a dónde va con el código y la descripción. Dos enlaces al mismo lugar serían dos
        // paradas de tabulador y la misma frase leída dos veces.
        aria-hidden
        tabIndex={-1}
        sx={{
          height: 180,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 1,
          position: 'relative',
          overflow: 'hidden',
          textDecoration: 'none',
          // El recuadro de la foto en blanco, como el fondo que traen las fotos del catálogo.
          // Sin esto heredaba el gris de la tarjeta y cada imagen se veía como un rectángulo
          // blanco pegoteado encima; con el mismo blanco, la foto y su recuadro son una cosa
          // sola y lo que se ve es el producto.
          bgcolor: '#fff',
          m: 1,
          mb: 0,
          borderRadius: 1.5,
        }}
      >
        {onVistaRapida && (
          <Tooltip title="Vista rápida">
            <IconButton
              className="vista-rapida"
              aria-label={`Vista rápida de ${producto.code}`}
              size="small"
              onClick={evento => {
                // La foto entera es un enlace a la ficha: sin esto, mirar de refilón te saca
                // de la página 7 de la grilla, que es justo lo que la vista rápida evita.
                evento.preventDefault();
                evento.stopPropagation();
                onVistaRapida(producto);
              }}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 2,
                opacity: 0,
                transition: 'opacity .2s ease',
                bgcolor: 'rgba(255,255,255,.92)',
                color: '#000',
                '&:hover': { bgcolor: '#fff' },
                // En pantalla táctil no hay "pasar por encima": si sólo apareciera al hover,
                // en el teléfono no existiría.
                '@media (hover: none)': { opacity: 1 },
              }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {foto ? (
          <Box
            component="img"
            className="foto-del-producto"
            src={foto}
            alt={producto.description}
            width={240}
            height={170}
            loading="lazy"
            // La grilla va de una columna en el teléfono a seis en escritorio. Sin `sizes` el
            // navegador supone el ancho de la ventana y baja la imagen más grande que haya
            // para todos los casos.
            sizes="(max-width: 600px) 92vw, (max-width: 900px) 46vw, (max-width: 1200px) 31vw, 240px"
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              // La foto de la tarjeta y la de la ficha son el mismo elemento a los ojos del
              // navegador: por eso una se convierte en la otra en vez de cortar.
              viewTransitionName: viajando ? 'foto-producto' : 'none',
              // Más lenta que la tarjeta a propósito: la foto sigue al movimiento en vez de
              // acompañarlo, y esa diferencia es lo que da la sensación de profundidad.
              transition: 'transform .45s cubic-bezier(.22,.61,.36,1)',
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              // El recuadro pasó a blanco, así que el texto gris claro de antes quedaba
              // ilegible sobre él.
              color: 'grey.500',
              gap: 0.5,
            }}
          >
            <ImageNotSupportedIcon sx={{ fontSize: 40, opacity: 0.45 }} />
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Imagen No Disponible
            </Typography>
          </Box>
        )}
      </Box>

      <Link
        to={ficha}
        viewTransition
        aria-label={`Ver detalle de ${producto.description} (${producto.code})`}
        style={{ textDecoration: 'none', color: 'inherit', height: '100%' }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/*
            Columna con `gap` y no dos elementos uno atrás del otro. El rubro es un `<span>` y el
            código también: apoyados en el `display` que traen por defecto quedaban en el mismo
            renglón y pegados -"ACCESORIOS VARIOSACL001"-. Antes no se notaba porque el código
            era un `<p>`, que corta línea solo; el salto de renglón dependía de un detalle del
            elemento y no de una decisión.
          */}
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                // Un antetítulo en mayúsculas y chico necesita aire entre letras para leerse
                // como una etiqueta y no como una palabra apretada.
                letterSpacing: '0.08em',
                lineHeight: 1.3,
              }}
            >
              {producto.category}
            </Typography>

            <CodigoDePieza
              codigo={producto.code}
              sx={{
                color: 'text.primary',
                viewTransitionName: viajando ? 'codigo-producto' : 'none',
              }}
            />
          </Box>

          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.4,
              }}
            >
              {producto.description}
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 1 }}>
            {conPrecios && precio !== null ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {mostrarOferta ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          color: 'text.secondary',
                          textDecoration: 'line-through',
                          fontSize: '0.9rem',
                        }}
                      >
                        {enPesos(precio)}
                      </Typography>

                      <Typography
                        variant="caption"
                        component="span"
                        sx={{ color: 'warning.main', fontWeight: 'bold' }}
                      >
                        -{enPorcentaje(descuento)}%
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      component="p"
                      sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1.1rem' }}
                    >
                      {enPesos(conOferta)}
                    </Typography>
                  </>
                ) : (
                  <Typography
                    variant="h6"
                    component="p"
                    sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1.1rem' }}
                  >
                    {enPesos(precio)}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                Consultar precio
              </Typography>
            )}
          </Box>

          <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {producto.is_in_stock ? (
              <Chip
                label="Stock Disponible"
                color="success"
                size="small"
                aria-label="Estado: stock disponible"
              />
            ) : (
              <Chip label="Sin Stock" color="error" size="small" aria-label="Estado: sin stock" />
            )}

            {estaEnOferta(producto) && (
              <Chip label="Oferta" color="warning" size="small" aria-label="Estado: oferta" />
            )}

            {producto.is_reposition && (
              <Chip
                label="Reposición"
                color="secondary"
                size="small"
                aria-label="Estado: reposicion"
              />
            )}

            {tieneDescuentoPorBulto(producto.bulk_discount) && (
              <Chip
                label="Descuento Bulto Cerrado"
                size="small"
                aria-label="Estado: descuento por bulto cerrado"
                sx={{ bgcolor: '#00897b', color: '#fff' }}
              />
            )}

            {producto.is_incoming && (
              <Chip label="Ingreso" color="info" size="small" aria-label="Estado: ingreso" />
            )}
          </Box>
        </Box>
      </Link>
    </Card>
  );
}
